import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { Plus, FileUp, Gauge, CheckCircle, XCircle, Wrench, Trash2 } from "lucide-react";
import { QuickStatsCard } from '@features/cx/shared/components/QuickStatsCard';
import { useMeterSummary, useMeterTypeCount } from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { AllMeters } from "./components/MeterDetails/tabs/allMeters";
import { ManualMeters } from "./components/MeterDetails/tabs/manualMeter";
import { PhotoMeters } from "./components/MeterDetails/tabs/photoMeters";
import { SmartMeters } from "./components/MeterDetails/tabs/smartMeters";

export const Meters: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: meterCounts, isLoading } = useMeterSummary(remoteUtilityId);

  const handleAddMeter = () => {
    navigate("new");
  };

  const handleBulkUpload = () => {
    navigate("/onboarding/data-migration/data-cards?tab=upload");
  };

  const { data: meterTypeCount } = useMeterTypeCount(remoteUtilityId);
  console.log('debug countssss', meterTypeCount)

  const tabComponents = {
    ALL: {
      label: "All Meters",
      shortLabel: "All",
      component: <AllMeters />,
      count: meterTypeCount?.totalMeterCount,
    },
    MANUAL_METER: {
      label: "Manual Meters",
      shortLabel: "Manual",
      component: <ManualMeters />,
      count: meterTypeCount?.manualMeter,
    },
    PHOTO_METER: {
      label: "Photo Meters",
      shortLabel: "Photo",
      component: <PhotoMeters />,
      count: meterTypeCount?.photoMeter,
    },
    SMART_METER: {
      label: "Smart Meters",
      shortLabel: "Smart",
      component: <SmartMeters />,
      count: meterTypeCount?.smartMeter,
    },
  };

  const urlMapping = {
    ALL: "all",
    MANUAL_METER: "manual",
    PHOTO_METER: "photo",
    SMART_METER: "smart",
  };

  // KPI Cards configuration using QuickStatsCard
  const meterKpiCards = [
    {
      title: "Total Meters",
      count: meterCounts?.total ?? meterCounts?.Total ?? 0,
      trendSummary: "Total number of meters in the system",
      trendDirection: "up" as const,
      icon: <Gauge className="h-8 w-8 text-blue-500" />
    },
    {
      title: "Assigned Meters",
      count: meterCounts?.assigned ?? meterCounts?.ASSIGNED ?? 0,
      trendSummary: "Meters currently assigned to consumers",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />
    },
    {
      title: "Unassigned Meters",
      count: meterCounts?.unassigned ?? meterCounts?.UNASSIGNED ?? 0,
      trendSummary: "Meters available for assignment",
      trendDirection: "down" as const,
      icon: <XCircle className="h-8 w-8 text-orange-500" />
    },
    {
      title: "Under Maintenance",
      count: meterCounts?.maintenance ?? meterCounts?.MAINTENANCE ?? 0,
      trendSummary: "Meters currently being serviced or repaired",
      trendDirection: "down" as const,
      icon: <Wrench className="h-8 w-8 text-yellow-500" />
    },
    {
      title: "Disposed",
      count: meterCounts?.disposed ?? meterCounts?.DISPOSED ?? 0,
      trendSummary: "Meters that have been decommissioned",
      trendDirection: "down" as const,
      icon: <Trash2 className="h-8 w-8 text-red-500" />
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
       <span>
         <h2 className="page-title">Meter Devices</h2>
        <p className="page-subtitle">
          Manage meter inventories, device types, and installation details.
        </p>
       </span>
       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div className="flex flex-wrap items-center justify-end w-full gap-2">
          <Button onClick={handleBulkUpload}>
            <FileUp className="mr-2 h-4 w-4" />
            Bulk Add Meter
          </Button>
          <Button onClick={handleAddMeter}>
            <Plus className="mr-2 h-4 w-4" />
            Add Meter
          </Button>
        </div>
      </div>
      </div>

      {/* KPI Summary Cards - Using loop with QuickStatsCard */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg p-6 shadow-sm animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {meterKpiCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
            />
          ))}
        </div>
      )}

      <Tabs
        defaultValue="ALL"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        tabsListClassName="grid w-full grid-cols-4 mb-6"
        idPrefix="meters-tab"
        level={0}
      />
    </div>
  );
};